import type {
    AnalyticsButtonInfo,
    AnalyticsEventImpression,
    AnalyticsPageImpression,
} from '@beat-venues/common-types';
import { useContext } from 'react';
import { useSearchParams } from 'react-router-dom';

import { AnalyticsContext, AnalyticsProvider } from '../../context';
import {
    createButtonInfo,
    createEventImpression,
    createPageImpression,
    createPageViewEvent,
} from '../../services';

interface ReturnType {
    triggerPageView: () => void;
    triggerButtonClick: (buttonInfo: AnalyticsButtonInfo) => void;
    triggerPageImpression: (pageInfo: AnalyticsPageImpression) => void;
    triggerEventImpression: (eventInfo: AnalyticsEventImpression) => void;
}

const useAnalytics = (): ReturnType => {
    const { pushEventToDataLayer } = useContext(AnalyticsContext);
    const [searchParams] = useSearchParams();

    if (!pushEventToDataLayer) {
        throw new Error(
            'useAnalytics must be used within an AnalyticsProvider'
        );
    }

    const triggerPageView = () => {
        const queryParams: Record<string, unknown> = Object.fromEntries(
            searchParams.entries()
        );
        pushEventToDataLayer(createPageViewEvent({ queryParams }));
    };

    const triggerButtonClick = (buttonInfo: AnalyticsButtonInfo) => {
        pushEventToDataLayer(createButtonInfo(buttonInfo));
    };

    const triggerPageImpression = (pageInfo: AnalyticsPageImpression) => {
        pushEventToDataLayer(createPageImpression(pageInfo));
    };

    const triggerEventImpression = (eventInfo: AnalyticsEventImpression) => {
        pushEventToDataLayer(createEventImpression(eventInfo));
    };

    return {
        triggerPageView,
        triggerButtonClick,
        triggerPageImpression,
        triggerEventImpression,
    };
};

export { AnalyticsProvider, useAnalytics };
